import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { teacherService } from '../../services/classroom/teacherService';
import { supabase } from '../../services/supabase';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 760px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  width: fit-content;
  text-decoration: none;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--primary);
  &:hover {
    text-decoration: underline;
  }
`;

const Card = styled.section`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: var(--surface);
  padding: 1.1rem;
`;

const Title = styled.h1`
  margin: 0 0 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text);
`;

const Field = styled.label`
  display: grid;
  gap: 0.45rem;
  margin-bottom: 0.9rem;
`;

const Label = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const Input = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
  background: var(--surface);
  color: var(--text);
  font-size: 0.92rem;
  font-family: inherit;
`;

const TextArea = styled.textarea`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
  background: var(--surface);
  color: var(--text);
  font-size: 0.9rem;
  min-height: 130px;
  resize: vertical;
  font-family: inherit;
`;

const ImageUploadArea = styled.label`
  display: grid;
  gap: 0.45rem;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.55rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary);
  background: var(--surfaceVariant);
  cursor: pointer;
`;

const UploadHint = styled.span`
  font-size: 0.76rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const ImagePreview = styled.div`
  width: 100%;
  max-width: 320px;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  overflow: hidden;
`;

const ImagePreviewTag = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.65rem;
`;

const PrimaryButton = styled.button`
  border: 0;
  border-radius: 10px;
  padding: 0.62rem 0.95rem;
  font-size: 0.86rem;
  font-weight: 700;
  font-family: inherit;
  background: var(--primary);
  color: var(--onPrimary);
  cursor: pointer;
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Link)`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.58rem 0.9rem;
  font-size: 0.86rem;
  font-weight: 600;
  text-decoration: none;
  color: var(--text);
  background: var(--surface);
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const ErrorText = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.84rem;
  color: var(--error, #dc2626);
  font-weight: 600;
`;

const ClassroomTeacherClassEditPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [classImageFile, setClassImageFile] = useState<File | null>(null);
  const [classImagePreview, setClassImagePreview] = useState<string | null>(null);
  const [existingClassImageUrl, setExistingClassImageUrl] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const classQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'class', classId],
    queryFn: () => teacherService.getClassById(classId),
    enabled: Boolean(classId),
  });

  useEffect(() => {
    if (!classQuery.data) return;
    setName(classQuery.data.name ?? '');
    setDescription(classQuery.data.description ?? '');
    setExistingClassImageUrl(classQuery.data.class_image_url ?? null);
  }, [classQuery.data]);

  useEffect(() => {
    return () => {
      if (classImagePreview) URL.revokeObjectURL(classImagePreview);
    };
  }, [classImagePreview]);

  const updateClass = useMutation({
    mutationFn: teacherService.updateClass,
    onSuccess: async (result) => {
      if (!result.ok) {
        setFormError(result.error);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'overview'] });
      await queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'class', classId] });
      navigate(`/classroom-teacher/classes/${encodeURIComponent(classId)}`);
    },
  });

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setFormError(null);
    let classImageUrl = existingClassImageUrl;
    if (classImageFile) {
      if (classImageFile.size > 5 * 1024 * 1024) {
        setFormError('Class image must be 5MB or smaller.');
        return;
      }
      const ext = classImageFile.name.split('.').pop() || 'jpg';
      const fileName = `classroom/class-images/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('post-images').upload(fileName, classImageFile, { upsert: false });
      if (uploadError) {
        setFormError(uploadError.message);
        return;
      }
      const { data: publicData } = supabase.storage.from('post-images').getPublicUrl(fileName);
      classImageUrl = publicData.publicUrl;
    }

    const result = await updateClass.mutateAsync({
      id: classId,
      name,
      description,
      classImageUrl,
    });
    if (!result.ok) {
      setFormError(result.error);
    }
  };

  if (classQuery.isLoading) return <Hint>Loading class settings…</Hint>;
  if (classQuery.isError || !classQuery.data) return <Hint>Unable to load class settings.</Hint>;

  return (
    <Page>
      <BackLink to={`/classroom-teacher/classes/${encodeURIComponent(classId)}`}>Back to Class</BackLink>
      <Card>
        <Title>Edit Class</Title>
        <form onSubmit={onSubmit} noValidate>
          {formError ? <ErrorText>{formError}</ErrorText> : null}
          <Field>
            <Label>Class Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="off" />
          </Field>
          <Field>
            <Label>Description</Label>
            <TextArea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1200} />
          </Field>
          <ImageUploadArea>
            <Label>Class Image</Label>
            <HiddenFileInput
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setClassImageFile(file);
                if (classImagePreview) URL.revokeObjectURL(classImagePreview);
                setClassImagePreview(file ? URL.createObjectURL(file) : null);
              }}
            />
            <UploadButton>Upload image</UploadButton>
            <UploadHint>PNG or JPG, up to 5MB.</UploadHint>
            {classImagePreview || existingClassImageUrl ? (
              <ImagePreview>
                <ImagePreviewTag src={classImagePreview ?? existingClassImageUrl ?? ''} alt="Class preview" />
              </ImagePreview>
            ) : null}
          </ImageUploadArea>
          <Actions>
            <PrimaryButton type="submit" disabled={updateClass.isPending}>
              {updateClass.isPending ? 'Saving…' : 'Save Changes'}
            </PrimaryButton>
            <SecondaryButton to={`/classroom-teacher/classes/${encodeURIComponent(classId)}`}>Cancel</SecondaryButton>
          </Actions>
        </form>
      </Card>
    </Page>
  );
};

export default ClassroomTeacherClassEditPage;
